import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { emailMask } from 'text-mask-addons';
import { useParams, useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { get, forEach, isNil } from 'lodash';

import Modal from '../../components/Modal';
import Popup from '../../components/Popup';
import Header from '../../components/Header';
import Button from '../../components/Button';
import Input from '../../components/Form/Input';
import Select from '../../components/Form/Select';
import Validator from '../../components/Validator';
import FormRow from '../../components/Form/FormRow';
import FormColumn from '../../components/Form/FormColumn';
import ApplicationId from '../../components/ApplicationId';
import VerifyButton from '../../components/Button/VerifyButton';
import { states } from '../../utils/states';
import { verifyAddress } from '../../utils/verifyAddress';
import { unsupportedStates, unSupportedStatesDPOnly } from '../../utils/unsupportedStates';
import { ricWorkflowName } from '../../utils/appConstants';
import { toTitleCase } from '../../utils/toTitleCase';
import { numberUnmask } from '../../utils/masks';
import { newNextAction } from '../../actions/navigation';
import imgError from '../../assets/images/error.svg';
import imgDeclined from '../../assets/images/declined.svg';
import { useConfig } from '../../config/ConfigContext';

import './style.scss';
import schema from './schema';

const MISMATCH_TITLES = {
  LastName: 'Last Name',
  Address: 'Address',
  DateOfBirth: 'Date of Birth',
  Ssn: 'Social Security Number',
  MobileNumber: 'Phone Number',
  Email: 'Email Address',
};

const Reprompting = (props) => {
  const appConfig = useConfig();
  const { navigate, validator: { validate, values, errors, setValues, onChangeHandler, isValid } } = props;
  const match = useParams();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessages, setErrorMessages] = useState(null);
  const [isAddressPopupVisible, setIsAddressPopupVisible] = useState(false);
  const [isAddressLoading, setIsAddressLoading] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isVerifyButtonClicked, setIsVerifyButtonClicked] = useState(false);
  const [popupType, setPopupType] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [prevAddresses, setPrevAddresses] = useState({});
  const [isPOBoxError, setIsPOBoxError] = useState(false);
  const [isEditingAddress, setIsEditingAddress] = useState(true);
  const checkinData = useSelector((state) => state.screen.checkinData);
  const mismatches = get(checkinData, 'data.data.mismatches', []);
  const workflowType = get(match, 'workflowType') === 'luwa' ? 'dtm-luwa' : get(match, 'workflowType');
  const key = searchParams.get('key');
  const isDPOnly = workflowType === 'dtm-luwa-dponly' || workflowType === ricWorkflowName;

  useEffect(() => {
    const sourceData = get(checkinData, ['data', 'data']) || get(checkinData, ['state', 'data']);
    if (isNil(sourceData)) {
      navigate(`/${workflowType === 'dtm-luwa' ? 'luwa' : workflowType}/checkin/?key=${key}`);
    }
  }, []);

  useEffect(() => {
    if (mismatches && mismatches.length > 0) {
      setValues({ ...values, mismatches });
    }
  }, [mismatches]);

  useEffect(() => {
    if (!get(errors, 'address') && !isVerified && appConfig.smartyStreetEnforce) {
      errors.address = isVerifyButtonClicked ? 'Address is not valid, please enter valid address' : 'Please click verify Address';
    }

    if (isPOBoxError) {
      errors.address = 'P.O. BOX address is not allowed';
    }
  }, [isValid]);

  const verifySmartyStreet = ({ address, city, state, zipcode }) => {
    setIsAddressLoading(true);
    setIsVerifyButtonClicked(true);

    verifyAddress({ zipcode, address, city, state }).then((response) => {
      if (response) {
        if (get(response, 'metadata.recordType') === 'P') {
          setIsAddressPopupVisible(true);
          setIsAddressLoading(false);
          setIsVerified(false);
          setPopupType(3);
          setIsFirstLoad(false);
        } else {
          const addressData = {
            ...values,
            address: response.deliveryLine1,
            city: get(response, 'components.cityName'),
            state: get(response, 'components.state'),
            zipcode: `${response.deliveryPointBarcode}`.slice(0, 5),
          };
          const hasOptional = `${get(response, 'analysis.footnotes', '')}`.indexOf('H#') !== -1;
          setIsVerified(true);
          setIsEditingAddress(false);
          setIsAddressLoading(false);
          setIsFirstLoad(false);
          setPrevAddresses({
            address: get(addressData, 'address'),
            city: get(addressData, 'city'),
            state: get(addressData, 'state'),
            zipcode: get(addressData, 'zipcode'),
          });
          setPopupType(hasOptional ? 5 : null);
          setIsAddressPopupVisible(hasOptional);
          setValues(addressData);
        }
      } else {
        setIsAddressPopupVisible(true);
        setIsAddressLoading(false);
        setIsFirstLoad(false);
        setIsVerified(false);
        setPopupType(2);
      }
    }).catch(() => {
      setIsAddressLoading(false);
      setPopupType(1);
      setIsFirstLoad(false);
      setIsVerified(false);
    });
  };

  const handleBlur = (event) => {
    event.preventDefault();

    switch (event.target.name) {
      case 'state':
        if (
          !(event.target.value === 'IA')
          && (!isDPOnly && unsupportedStates.indexOf(event.target.value) !== -1
          || isDPOnly && unSupportedStatesDPOnly.indexOf(event.target.value) !== -1)
        ) {
          setIsAddressPopupVisible(true);
          setIsAddressLoading(false);
          setPopupType(4);
        } else if (isFirstLoad && get(values, 'city') && get(values, 'address')) {
          verifySmartyStreet({
            zipcode: get(values, 'zipcode'),
            address: get(values, 'address'),
            city: get(values, 'city'),
            state: event.target.value,
          });
        }
        break;
      case 'zipcode':
        if (isFirstLoad && get(values, 'city') && get(values, 'state') && get(values, 'address')) {
          verifySmartyStreet({
            zipcode: event.target.value,
            address: get(values, 'address'),
            city: get(values, 'city'),
            state: get(values, 'state'),
          });
        }
        onChangeHandler(event.target.name, toTitleCase(event.target.value));
        break;
      case 'address':
        if (isFirstLoad && get(values, 'city') && get(values, 'state')) {
          verifySmartyStreet({
            zipcode: get(values, 'zipcode'),
            address: toTitleCase(event.target.value),
            city: get(values, 'city'),
            state: get(values, 'state'),
          });
        }
        onChangeHandler(event.target.name, toTitleCase(event.target.value));
        break;
      case 'city':
        if (isFirstLoad && get(values, 'address') && get(values, 'state')) {
          verifySmartyStreet({
            zipcode: get(values, 'zipcode'),
            address: get(values, 'address'),
            city: toTitleCase(event.target.value),
            state: get(values, 'state'),
          });
        }
        onChangeHandler(event.target.name, toTitleCase(event.target.value));
        break;
      default:
    }
  };

  const handleAbort = () => {
    setIsAddressPopupVisible(false);
  };

  const handleVerifyAddressClick = (e) => {
    e.preventDefault();

    if (!get(values, 'address')) {
      setIsAddressPopupVisible(true);
      setPopupType(1);
      setIsVerifyButtonClicked(true);
    } else {
      verifySmartyStreet({
        address: get(values, 'address'),
        city: get(values, 'city'),
        state: get(values, 'state'),
        zipcode: get(values, 'zipcode'),
      });
    }
  };

  const handleUndo = (e) => {
    e.preventDefault();

    const addressData = {
      ...values,
      address: get(prevAddresses, 'address'),
      city: get(prevAddresses, 'city'),
      state: get(prevAddresses, 'state'),
      zipcode: get(prevAddresses, 'zipcode'),
    };

    setIsVerified(false);
    setValues(addressData);
  };

  const submitMismatch = () => {
    setErrorMessages(null);
    setHasError(false);

    if (validate(schema).isValid) {
      setIsLoading(true);

      const data = {};
      forEach(mismatches, (field) => {
        if (field === 'Address') {
          data.Address = {
            StreetAddress: get(values, 'address'),
            City: get(values, 'city'),
            State: get(values, 'state'),
            ZipCode: numberUnmask(get(values, 'zipcode')),
          };
        } else if (field === 'Ssn' || field === 'MobileNumber') {
          data[field] = numberUnmask(get(values, field));
        } else {
          data[field] = get(values, field);
        }
      });

      if (key) {
        dispatch(newNextAction({
          data,
          key,
          workflowType,
          success: (response) => {
            setIsLoading(false);
            const routeUrl = response.data && response.data.url;
            navigate(routeUrl);
          },
          fail: (error) => {
            setIsLoading(false);
            const possibleErrors = get(error, 'data.failure.errors') || [];
            setHasError(true);
            if (get(error, 'status') === 400 && possibleErrors && possibleErrors.length > 0) {
              setErrorMessages(error.data.failure.errors && error.data.failure.errors.map((err) => err.message || ''));
            } else {
              setErrorMessages(['An unexpected error occurred. Please try again.']);
            }
          },
        }));
      }
    } else {
      console.log('api error', errors, 'address is not verified');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (mismatches.indexOf('Address') !== -1) {
      const addressToVerify = {
        zipcode: get(values, 'zipcode'),
        address: get(values, 'address'),
        city: get(values, 'city'),
        state: get(values, 'state'),
      };

      verifyAddress(addressToVerify).then((res) => {
        if (res) {
          if (get(res, 'metadata.recordType') === 'P') {
            setIsLoading(false);
            setIsPOBoxError(true);
          } else {
            submitMismatch();
          }
        } else {
          submitMismatch();
        }
      }).catch(() => {
        submitMismatch();
      });
    } else {
      submitMismatch();
    }
  };

  const handleInputChange = (event) => {
    onChangeHandler(event.target.name, event.target.value);
  };

  const renderMismatchField = (field) => {
    switch (field) {
      case 'LastName':
        return (
          <Input
            label="Last Name"
            name="LastName"
            value={values.LastName}
            onChange={handleInputChange}
            isRequired
            hasError={!!errors.LastName}
            isErrorVisible={!!errors.LastName}
            errorMessage={errors.LastName}
          />
        );
      case 'Ssn':
        return (
          <Input
            label="SSN"
            name="Ssn"
            isMasked={[/\d/, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
            value={values.Ssn}
            onChange={handleInputChange}
            isRequired
            hasError={!!errors.Ssn}
            isErrorVisible={!!errors.Ssn}
            errorMessage={errors.Ssn}
          />
        );
      case 'MobileNumber':
        return (
          <Input
            label="Phone Number"
            name="MobileNumber"
            isMasked={['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
            value={values.MobileNumber}
            onChange={handleInputChange}
            isRequired
            hasError={!!errors.MobileNumber}
            isErrorVisible={!!errors.MobileNumber}
            errorMessage={errors.MobileNumber}
          />
        );
      case 'Email':
        return (
          <Input
            label="Email Address"
            name="Email"
            isMasked={emailMask}
            value={values.Email}
            onChange={handleInputChange}
            isRequired
            hasError={!!errors.Email}
            isErrorVisible={!!errors.Email}
            errorMessage={errors.Email}
            className="primaryBlue"
          />
        );
      case 'DateOfBirth':
        return (
          <Input
            label="Date of Birth"
            name="DateOfBirth"
            isMasked={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
            value={values.DateOfBirth}
            onChange={handleInputChange}
            isRequired
            hasError={!!errors.DateOfBirth}
            isErrorVisible={!!errors.DateOfBirth}
            errorMessage={errors.DateOfBirth}
          />
        );
      case 'Address':
        return isEditingAddress ? (
          <div>
            <FormRow className="mb-2">
              <FormColumn className="positionRelative">
                <Input
                  label="Street Address"
                  name="address"
                  value={get(values, 'address', '')}
                  onChange={handleInputChange}
                  isRequired
                  hasError={!!errors.address}
                  errorMessage={errors.address}
                  onBlur={handleBlur}
                  className="streetAddress"
                />
                <VerifyButton
                  onClick={isVerified ? handleUndo : handleVerifyAddressClick}
                  isVerified={isVerified}
                  isLoading={isAddressLoading}
                />
                { isAddressPopupVisible
                  && (
                  <Popup
                    handleAbort={handleAbort}
                    type={popupType}
                    data={{
                      address: get(values, 'address'),
                      city: get(values, 'city'),
                      state: get(values, 'state'),
                      zipcode: get(values, 'zipcode'),
                    }}
                  />
                  )}
              </FormColumn>
            </FormRow>
            <FormRow className="mb-2">
              <FormColumn>
                <Input
                  label="City"
                  name="city"
                  value={values.city}
                  onChange={handleInputChange}
                  isRequired
                  hasError={!!errors.city}
                  errorMessage={errors.city}
                  onBlur={handleBlur}
                />
              </FormColumn>
            </FormRow>
            <FormRow>
              <FormColumn>
                <FormRow>
                  <FormColumn>
                    <Select
                      name="state"
                      data={states}
                      value={values.state}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      label="State"
                      isRequired
                      hasError={!!errors.state}
                      errorMessage={errors.state}
                    />
                  </FormColumn>
                  <FormColumn>
                    <Input
                      label="Zip"
                      isMasked={[/\d/, /\d/, /\d/, /\d/, /\d/]}
                      name="zipcode"
                      value={values.zipcode}
                      onChange={handleInputChange}
                      isRequired
                      hasError={!!errors.zipcode}
                      errorMessage={errors.zipcode}
                    />
                  </FormColumn>
                </FormRow>
              </FormColumn>
            </FormRow>
          </div>
        ) : (
          <div>
            <FormRow className="mb-2">
              <FormColumn>
                <p className="text-left">
                  {values.address}
                  {' '}
                  <br />
                  {values.city}
                  ,
                  {' '}
                  {values.state}
                  {' '}
                  {values.zipcode}
                </p>
              </FormColumn>
            </FormRow>
          </div>
        );
      default:
        return null;
    }
  };

  const clearErrors = () => {
    setHasError(false);
    setErrorMessages(null);
  };

  const handleEditClick = (e) => {
    e && e.preventDefault();
    if (isEditingAddress) {
      setValues({
        ...values,
        address: get(prevAddresses, 'address'),
        city: get(prevAddresses, 'city'),
        state: get(prevAddresses, 'state'),
        zipcode: get(prevAddresses, 'zipcode'),
      });
    }
    setIsEditingAddress(!isEditingAddress);
  };

  const renderCards = () => (
    <div className="container-fluid">
      {mismatches.map((field, index) => (
        <FormRow key={index}>
          <FormColumn>
            <div className="card dark-shadow">
              <div className="card-header">
                <span>{index + 1}</span>
                {MISMATCH_TITLES[field] || field}
                {field === 'Address' && !isFirstLoad && (
                  <button type="button" className="btn-sm" onClick={handleEditClick}>
                    {isEditingAddress ? 'Cancel' : 'Edit'}
                  </button>
                )}
              </div>
              <div className="card-body">
                {renderMismatchField(field)}
              </div>
            </div>
          </FormColumn>
        </FormRow>
      ))}
    </div>
  );

  return (
    <div className="reprompt-screen transition-item wrapper v-center">
      <Header navigate={props.navigate} />
      {get(checkinData, 'attributes.entityId') ? (
        <ApplicationId id={get(checkinData, 'attributes.entityId')} style={{ marginTop: 10 }} />
      ) : null}
      <div className="content" style={{ minHeight: 'calc(100vh - 210px)' }}>
        <img src={imgDeclined} className="mb-2" alt="Declined" width={80} />
        <h1 className="mb-1">We had a difficult time verifying you</h1>
        <p className="mb-3">
          We found
          {' '}
          {mismatches.length}
          {' '}
          discrepanc
          {mismatches.length !== 1 ? 'ies' : 'y'}
          .
          {' '}
          Please review and confirm.
        </p>
        {renderCards()}
        <div className="container-fluid">
          <div className="footer">
            <FormRow>
              <FormColumn>
                <Button
                  color="primary"
                  className="large buttonStyle"
                  onClick={handleSubmit}
                  isLoading={isLoading}
                  isDisabled={isLoading}
                  title="Submit"
                />
              </FormColumn>
            </FormRow>
            <FormRow>
              <FormColumn>
                <p>
                  If you need assistance, please contact
                  <br />
                  customer support at
                  {' '}
                  <a href="tel:18006978743">(800) 697-8743</a>
                  .
                </p>
              </FormColumn>
            </FormRow>
          </div>
        </div>
      </div>
      {hasError && (
        <Modal>
          <div className="card-content error-modal security-error">
            <img src={imgError} alt="" />
            <div className="errorsContainer">
              {errorMessages.map((err, index) => (<p key={index}>{err}</p>))}
            </div>
            <div onClick={clearErrors} className="btn btn-red">OK</div>
          </div>
        </Modal>
      )}
    </div>
  );
};

Reprompting.propTypes = {
  navigate: PropTypes.func.isRequired,
  validator: PropTypes.object.isRequired,
};

export default Validator(schema)(Reprompting);
